import {
  CampaignDetailModelState,
  CommissionDetails,
  ListingAgreementDetails,
  StaffUpdateAgreementDetailsForCampaignRequest,
  StaffUpdateCommissionInfoForCampaignRequest,
  StaffUpdateCommissionInfoForCampaignRequestChangeType,
  useInvalidateUseStaffGetAgreementDetailsForCampaign,
  useInvalidateUseStaffGetCommissionInfoForCampaign,
  useGetCampaign,
  useStaffGetAgreementDetailsForCampaign,
  useStaffGetCommissionInfoForCampaign,
  useStaffGetDocumentsForCampaign,
  useStaffUpdateAgreementDetailsForCampaign,
  useStaffUpdateCommissionInfoForCampaign,
} from '@urbanx/agx.campaigns.client'
import ComplianceModal from './ComplianceModal'
import {
  AutopilotTaskDto,
  TaskCategoryTaskType,
} from '@urbanx/agx.activities.client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { filterDocumentGroups } from 'pages/campaigns/components/campaignTasks/AgreementCompliance/AgreementComplianceHelper'
import { AgxLoadingState } from '@urbanx/agx.ui-components2'
import CompliancePages from './CompliancePages'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import { createCampaignDetailsQueryKey } from 'hooks/useSelectedCampaign'
import { BaseComplianceModalProps } from './BaseComplianceModalProps'

interface AgreementComplianceModalProps {
  campaignId: string
  isOpen: boolean
  onClose: () => void
  tasks: AutopilotTaskDto[]
}

const AgreementComplianceModal = ({
  campaignId,
  isOpen,
  onClose,
  tasks,
}: AgreementComplianceModalProps) => {
  const queryClient = useQueryClient()
  const { data: campaign, isPending: isCampaignPending } = useGetCampaign(
    campaignId,
    !!campaignId
  )

  const { data: documentGroups, isPending: documentsPending } =
    useStaffGetDocumentsForCampaign(
      campaign?.campaignId || '',
      !!campaign?.campaignId
    )

  const filteredDocuments = useMemo(
    () =>
      documentGroups && Array.isArray(documentGroups)
        ? //TODO: filter document groups is currently agency agreement specific
          filterDocumentGroups(documentGroups)
        : [],
    [documentGroups]
  )

  const { data: agreementDetailsData, isPending: agreementDetailsPending } =
    useStaffGetAgreementDetailsForCampaign(campaignId, !!campaignId && isOpen)
  const {
    data: commissionInformation,
    isPending: commissionInformationPending,
  } = useStaffGetCommissionInfoForCampaign(campaignId, !!campaignId && isOpen)

  const currentCommissionInfo = useMemo(
    () =>
      commissionInformation?.commissionInformation?.currentCommissionDetails
        ?.commissionDetails,
    [commissionInformation]
  )

  const [agreementDetails, setAgreementDetails] = useState<
    ListingAgreementDetails | undefined
  >()
  const [agreementDetailsDirty, setAgreementDetailsDirty] = useState(false)
  const [commissionDetails, setCommissionDetails] = useState<
    CommissionDetails | undefined
  >()
  const [commissionDetailsDirty, setCommissionDetailsDirty] = useState(false)
  const [commissionDetailsValid, setCommissionDetailsValid] = useState(false)

  useEffect(() => {
    if (agreementDetailsDirty) {
      return
    }

    setAgreementDetails(agreementDetailsData)
  }, [agreementDetailsData, agreementDetailsDirty])

  useEffect(() => {
    if (commissionDetailsDirty) {
      return
    }

    setCommissionDetails(currentCommissionInfo)
  }, [commissionDetailsDirty, currentCommissionInfo])

  const invalidateAgreementDetails =
    useInvalidateUseStaffGetAgreementDetailsForCampaign()
  const invalidateCommissionInfo =
    useInvalidateUseStaffGetCommissionInfoForCampaign()

  const { staffUpdateAgreementDetailsForCampaign } =
    useStaffUpdateAgreementDetailsForCampaign({
      onSuccess: async () => {
        await invalidateAgreementDetails(campaignId)
        queryClient.invalidateQueries({
          queryKey: createCampaignDetailsQueryKey(campaignId),
        })
      },
      onError: () => {},
    })

  const { staffUpdateCommissionInfoForCampaign } =
    useStaffUpdateCommissionInfoForCampaign({
      onSuccess: async () => {
        await invalidateCommissionInfo(campaignId)
        queryClient.invalidateQueries({
          queryKey: createCampaignDetailsQueryKey(campaignId),
        })
      },
      onError: () => {},
    })

  const onAgreementDetailsUpdated = useCallback(
    (updated: { id: string; value: ListingAgreementDetails }) => {
      const hasChanged =
        JSON.stringify(agreementDetails) !== JSON.stringify(updated.value)

      setAgreementDetails((previous) => {
        if (JSON.stringify(previous) === JSON.stringify(updated.value)) {
          return previous
        }

        return updated.value
      })

      if (hasChanged) {
        setAgreementDetailsDirty(true)
      }
    },
    [agreementDetails]
  )

  const onCommissionUpdated = useCallback(
    (updated: { value: CommissionDetails }) => {
      const hasChanged =
        JSON.stringify(commissionDetails) !== JSON.stringify(updated.value)

      setCommissionDetails((previous) => {
        if (JSON.stringify(previous) === JSON.stringify(updated.value)) {
          return previous
        }

        return updated.value
      })

      if (hasChanged) {
        setCommissionDetailsDirty(true)
      }
    },
    [commissionDetails]
  )

  const isAgreementTypeOpen =
    agreementDetails?.agreementType?.toLowerCase() === 'open'

  const {
    hasAgreementType,
    hasAgreementStart,
    hasAgreementEnd,
    agreementDatesOrdered,
    agreementExceededGap,
  } = useMemo(() => {
    const hasType = Boolean(agreementDetails?.agreementType)
    const hasStart = Boolean(agreementDetails?.startDate)
    const hasEnd = Boolean(agreementDetails?.endDate)
    const datesOrdered =
      !agreementDetails?.startDate ||
      !agreementDetails?.endDate ||
      dayjs(agreementDetails.endDate).diff(
        dayjs(agreementDetails.startDate),
        'day'
      ) >= 0
    const exceededGap =
      !isAgreementTypeOpen &&
      hasStart &&
      hasEnd &&
      dayjs(agreementDetails?.endDate).diff(
        dayjs(agreementDetails?.startDate),
        'day'
      ) > 90

    return {
      hasAgreementType: hasType,
      hasAgreementStart: hasStart,
      hasAgreementEnd: hasEnd,
      agreementDatesOrdered: datesOrdered,
      agreementExceededGap: exceededGap,
    }
  }, [agreementDetails, isAgreementTypeOpen])

  const {
    hasAgreementDetailsTask,
    hasAgreementTypeTask,
    hasCommissionTask,
  } = useMemo(
    () => ({
      hasAgreementDetailsTask: tasks.some(
        (task) =>
          task.category?.taskType === TaskCategoryTaskType.AgreementDetails
      ),
      hasAgreementTypeTask: tasks.some(
        (task) =>
          task.category?.taskType === TaskCategoryTaskType.AgreementComplianceType
      ),
      hasCommissionTask: tasks.some(
        (task) =>
          task.category?.taskType === TaskCategoryTaskType.AgreementCommission
      ),
    }),
    [tasks]
  )

  const qldAgreementValidationPassed =
    !hasAgreementTypeTask ||
    (hasAgreementType &&
      hasAgreementStart &&
      (isAgreementTypeOpen || hasAgreementEnd) &&
      agreementDatesOrdered &&
      !agreementExceededGap)

  const nswAgreementValidationPassed =
    !hasAgreementDetailsTask ||
    (hasAgreementStart && hasAgreementEnd && agreementDatesOrdered)

  const qldCommissionValidationPassed =
    !hasCommissionTask || commissionDetailsValid
  const nswCommissionValidationPassed =
    !hasAgreementTypeTask || commissionDetailsValid

  const completeDisabled = useMemo(() => {
    switch (campaign?.state) {
      case CampaignDetailModelState.QLD:
        return !(qldAgreementValidationPassed && qldCommissionValidationPassed)
      case CampaignDetailModelState.NSW:
        return !(nswAgreementValidationPassed && nswCommissionValidationPassed)
      default:
        return false
    }
  }, [
    campaign?.state,
    nswAgreementValidationPassed,
    nswCommissionValidationPassed,
    qldAgreementValidationPassed,
    qldCommissionValidationPassed,
  ])

  const saveAgreementDetails = useCallback(async () => {
    if (!agreementDetailsDirty || !agreementDetails) {
      return
    }

    await staffUpdateAgreementDetailsForCampaign({
      campaignId,
      agreementDetails,
    } as StaffUpdateAgreementDetailsForCampaignRequest)
    setAgreementDetailsDirty(false)
  }, [
    agreementDetails,
    agreementDetailsDirty,
    campaignId,
    staffUpdateAgreementDetailsForCampaign,
  ])

  const saveCommissionDetails = useCallback(async () => {
    if (!commissionDetailsDirty || !commissionDetails) {
      return
    }

    await staffUpdateCommissionInfoForCampaign({
      campaignId,
      commissionDetails,
      changeType: StaffUpdateCommissionInfoForCampaignRequestChangeType.Other,
      reason: 'Agreement Compliance Completion',
    } as StaffUpdateCommissionInfoForCampaignRequest)
    setCommissionDetailsDirty(false)
  }, [
    campaignId,
    commissionDetails,
    commissionDetailsDirty,
    staffUpdateCommissionInfoForCampaign,
  ])

  const onSaveCurrentPage = useCallback(
    async (task: AutopilotTaskDto) => {
      if (!campaign) {
        return
      }

      if (task.category?.taskType === TaskCategoryTaskType.AgreementDetails) {
        if (campaign.state === CampaignDetailModelState.NSW) {
          await saveAgreementDetails()
        }
        return
      }

      if (
        task.category?.taskType === TaskCategoryTaskType.AgreementComplianceType
      ) {
        if (campaign.state === CampaignDetailModelState.QLD) {
          await saveAgreementDetails()
        }

        if (campaign.state === CampaignDetailModelState.NSW) {
          await saveCommissionDetails()
        }
        return
      }

      if (task.category?.taskType === TaskCategoryTaskType.AgreementCommission) {
        if (campaign.state === CampaignDetailModelState.QLD) {
          await saveCommissionDetails()
        }
      }
    },
    [campaign, saveAgreementDetails, saveCommissionDetails]
  )

  const renderPage = useCallback(
    (props: BaseComplianceModalProps) =>
      isCampaignPending ||
      documentsPending ||
      agreementDetailsPending ||
      commissionInformationPending ? (
        <AgxLoadingState />
      ) : (
        <CompliancePages
          {...props}
          agreementDetails={agreementDetails}
          onAgreementDetailsUpdated={onAgreementDetailsUpdated}
          commissionDetails={commissionDetails}
          currentCommissionInfo={currentCommissionInfo}
          setCommissionDetailsValid={setCommissionDetailsValid}
          onCommissionUpdated={onCommissionUpdated}
          isAgreementTypeOpen={isAgreementTypeOpen}
          agreementExceededGap={agreementExceededGap}
        />
      ),
    [
      agreementDetails,
      agreementDetailsPending,
      agreementExceededGap,
      commissionDetails,
      commissionInformationPending,
      currentCommissionInfo,
      documentsPending,
      isAgreementTypeOpen,
      isCampaignPending,
      onAgreementDetailsUpdated,
      onCommissionUpdated,
    ]
  )

  return (
    <ComplianceModal
      title={'Agreement Compliance'}
      tasks={tasks}
      open={isOpen}
      onClose={onClose}
      documents={filteredDocuments}
      campaignId={campaignId}
      campaign={campaign!}
      onSaveCurrentPage={onSaveCurrentPage}
      completeDisabled={completeDisabled}
      renderPage={renderPage}
    />
  )
}

export default AgreementComplianceModal
